import { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronLeft, ChevronRight } from "@digital-twins/design-system/icons";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

export interface SocialBoardRailItem {
  /** A theme id or SOCIAL_OTHER_THEME_ID. */
  id: string;
  label: string;
  /** The board's figure in the current measure, already formatted. */
  valueLabel: string;
}

interface SocialBoardRailProps {
  /** In board order (the current measure). The first 3 show, the rest go in a "More" menu. */
  items: Array<SocialBoardRailItem>;
  activeId: string | null;
  onSelect: (id: string) => void;
  /** Absent hides the previous arrow (a rail that only jumps, such as a collection's kinds). */
  onPrevious?: () => void;
  /** Absent hides the next arrow. */
  onNext?: () => void;
  /**
   * Chips shown before "More". When absent, the count follows the available
   * width (as many chips as fit); where no width can be measured it falls
   * back to `SOCIAL_BOARD_RAIL_VISIBLE`.
   */
  visibleCount?: number;
  /** The chip group's label. Defaults to the Topic boards' label. */
  ariaLabel?: string;
  /** Test ids are `${testIdPrefix}`, `-${id}`, `-more` and `-more-${id}`. Defaults to `social-board-rail`. */
  testIdPrefix?: string;
}

/** Chips the rail shows before "More" when no width can be measured. */
export const SOCIAL_BOARD_RAIL_VISIBLE = 3;

const GAP_PX = 6;

const CHIP_CLASS =
  "inline-flex h-8 shrink-0 items-center gap-2 whitespace-nowrap rounded-full border border-border bg-background px-3 text-[13px] text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

const ICON_BUTTON_CLASS =
  "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border bg-background text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-40";

/**
 * The boards' rail: as many boards as fit the width as chips with their
 * figure, the rest in a "More" menu, and the previous / next arrows when
 * their handlers are given. The active board's chip
 * carries a stronger outline (never a fill).
 */
export function SocialBoardRail(props: SocialBoardRailProps) {
  const {
    items,
    activeId,
    onSelect,
    onPrevious,
    onNext,
    visibleCount: visibleCountProp,
    ariaLabel,
    testIdPrefix = "social-board-rail",
  } = props;

  const { t } = useTranslation("app");

  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const arrowsRef = useRef<HTMLDivElement>(null);
  const [fittedCount, setFittedCount] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (visibleCountProp != null) {
      return undefined;
    }
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) {
      return undefined;
    }
    const recompute = () => {
      const available =
        container.clientWidth - (arrowsRef.current?.offsetWidth ?? 0) - (arrowsRef.current ? GAP_PX : 0);
      const chips = Array.from(measure.children) as Array<HTMLElement>;
      const moreChip = chips.pop();
      const widths = chips.map((chip) => chip.offsetWidth);
      if (available <= 0 || widths.length === 0 || widths.every((width) => width === 0)) {
        setFittedCount(null);
        return;
      }
      const moreWidth = moreChip?.offsetWidth ?? 0;
      const fitting = (limit: number) => {
        let used = 0;
        let count = 0;
        for (const width of widths) {
          const next = used + width + (count > 0 ? GAP_PX : 0);
          if (next > limit) {
            break;
          }
          used = next;
          count += 1;
        }
        return count;
      };
      let count = fitting(available);
      if (count < widths.length) {
        // Some go in "More": leave room for its own chip.
        count = Math.max(1, fitting(available - moreWidth - GAP_PX));
      }
      setFittedCount(count);
    };
    recompute();
    const observer = new ResizeObserver(recompute);
    observer.observe(container);
    return () => observer.disconnect();
  }, [items, visibleCountProp, onPrevious, onNext]);

  const visibleCount = visibleCountProp ?? fittedCount ?? SOCIAL_BOARD_RAIL_VISIBLE;
  const visible = items.slice(0, visibleCount);
  const overflow = items.slice(visibleCount);
  const isActiveInOverflow = overflow.some((item) => item.id === activeId);
  const hasSteps = items.length > 1;
  const hasArrows = onPrevious != null || onNext != null;

  return (
    <div
      ref={containerRef}
      className="relative flex w-full min-w-0 items-center gap-1.5"
      data-testid={testIdPrefix}
    >
      {visibleCountProp == null ? (
        // Off-screen copy of every chip (and "More"), measured to decide how many fit.
        <div
          ref={measureRef}
          aria-hidden="true"
          className="pointer-events-none invisible absolute left-0 top-0 flex h-0 w-0 gap-1.5 overflow-hidden"
        >
          {items.map((item) => (
            <span key={item.id} className={CHIP_CLASS}>
              <span className="max-w-[220px] truncate">{item.label}</span>
              <span className="tabular-nums">{item.valueLabel}</span>
            </span>
          ))}
          <span className={CHIP_CLASS}>
            {t("socialListening.topicPage.boards.more")}
            <span className="tabular-nums">{items.length}</span>
            <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
          </span>
        </div>
      ) : null}
      <div
        className="flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto p-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        aria-label={ariaLabel ?? t("socialListening.topicPage.boards.ariaLabel")}
        role="group"
      >
        {visible.map((item) => {
          const isActive = item.id === activeId;
          return (
            <button
              key={item.id}
              type="button"
              className={cn(CHIP_CLASS, "shrink min-w-0", isActive && "border-foreground/60")}
              aria-current={isActive ? "true" : undefined}
              data-testid={`${testIdPrefix}-${item.id}`}
              onClick={() => onSelect(item.id)}
            >
              <span className="min-w-0 max-w-[220px] truncate">{item.label}</span>
              <span className="tabular-nums text-muted-foreground">
                {item.valueLabel}
              </span>
            </button>
          );
        })}
        {overflow.length > 0 ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className={cn(
                  CHIP_CLASS,
                  isActiveInOverflow && "border-foreground/60",
                )}
                aria-current={isActiveInOverflow ? "true" : undefined}
                data-testid={`${testIdPrefix}-more`}
              >
                {t("socialListening.topicPage.boards.more")}
                <span className="tabular-nums text-muted-foreground">
                  {overflow.length}
                </span>
                <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="min-w-[240px] rounded-xl"
            >
              {overflow.map((item) => (
                <DropdownMenuItem
                  key={item.id}
                  className="flex items-center justify-between gap-4"
                  aria-current={item.id === activeId ? "true" : undefined}
                  data-testid={`${testIdPrefix}-more-${item.id}`}
                  onSelect={() => onSelect(item.id)}
                >
                  <span className="truncate">{item.label}</span>
                  <span className="tabular-nums text-muted-foreground">
                    {item.valueLabel}
                  </span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
      {hasArrows ? (
        <div ref={arrowsRef} className="ml-auto flex shrink-0 items-center gap-1.5">
          {onPrevious ? (
            <button
              type="button"
              className={ICON_BUTTON_CLASS}
              aria-label={t("socialListening.topicPage.boards.previous")}
              disabled={!hasSteps}
              data-testid="social-boards-previous"
              onClick={onPrevious}
            >
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            </button>
          ) : null}
          {onNext ? (
            <button
              type="button"
              className={ICON_BUTTON_CLASS}
              aria-label={t("socialListening.topicPage.boards.next")}
              disabled={!hasSteps}
              data-testid="social-boards-next"
              onClick={onNext}
            >
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
